import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HardHat, ExternalLink, Wrench, MapPin, Calendar, ClipboardCheck } from 'lucide-react';

const STATUS_STYLES = {
  da_pianificare: { label: 'Da pianificare', variant: 'secondary' },
  pianificato: { label: 'Pianificato', variant: 'default' },
  in_corso: { label: 'In corso', variant: 'default' },
  completato: { label: 'Completato', variant: 'outline' },
  verificato: { label: 'Verificato', variant: 'outline' },
  annullato: { label: 'Annullato', variant: 'destructive' },
};

export default function InstallerInterventi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const r = await api.listInstallations(params);
      setItems(r.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento interventi');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Aggregazioni rapide
  const summary = {
    total: items.length,
    da_pianificare: items.filter(i => i.status === 'da_pianificare').length,
    pianificato: items.filter(i => i.status === 'pianificato').length,
    in_corso: items.filter(i => i.status === 'in_corso').length,
    completato: items.filter(i => i.status === 'completato').length,
  };
  const percentChecklist = (it) => {
    const cl = it.checklist || [];
    if (!cl.length) return 0;
    const done = cl.filter(x => x.status === 'uploaded' || x.status === 'verified').length;
    return Math.round(100 * done / cl.length);
  };

  return (
    <div>
      <PageHeader
        title="I miei Interventi"
        description="Elenco degli interventi di installazione assegnati a te. Apri l'intervento per completare la checklist documentale (foto + PDF)."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="installer-summary">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground flex items-center gap-1"><HardHat className="size-3.5" />Totali</div>
          <div className="text-2xl font-semibold tabular-nums mt-1">{summary.total}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Da pianificare</div>
          <div className="text-2xl font-semibold tabular-nums mt-1 text-orange-500">{summary.da_pianificare}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">In corso</div>
          <div className="text-2xl font-semibold tabular-nums mt-1 text-primary">{summary.in_corso + summary.pianificato}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-muted-foreground">Completati</div>
          <div className="text-2xl font-semibold tabular-nums mt-1">{summary.completato}</div>
        </CardContent></Card>
      </div>

      <Card>
        <div className="p-3 border-b flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="installer-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="da_pianificare">Da pianificare</SelectItem>
              <SelectItem value="pianificato">Pianificato</SelectItem>
              <SelectItem value="in_corso">In corso</SelectItem>
              <SelectItem value="completato">Completato</SelectItem>
              <SelectItem value="verificato">Verificato</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <div className="text-xs text-muted-foreground">{items.length} interventi</div>
        </div>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Caricamento…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto size-12 rounded-full bg-secondary grid place-items-center mb-3"><Wrench className="size-5 text-muted-foreground" /></div>
              <div className="text-sm font-medium">Nessun intervento assegnato</div>
              <div className="text-xs text-muted-foreground">Gli interventi ti verranno assegnati dallo staff Scintilla dopo la firma dei contratti.</div>
            </div>
          ) : (
            <Table data-testid="installer-interventi-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Numero</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Località</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Progresso</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(it => {
                  const st = STATUS_STYLES[it.status] || { label: it.status, variant: 'secondary' };
                  const cust = it.customer_snapshot || {};
                  const custName = cust.customer_type === 'azienda'
                    ? cust.company_name
                    : `${cust.first_name || ''} ${cust.last_name || ''}`.trim();
                  const perc = percentChecklist(it);
                  return (
                    <TableRow key={it.id} data-testid={`intervento-row-${it.id}`}>
                      <TableCell className="font-mono text-xs">{it.number || '—'}</TableCell>
                      <TableCell className="font-medium">{custName || '—'}</TableCell>
                      <TableCell><span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="size-3" />{it.site_city}, {it.site_province}</span></TableCell>
                      <TableCell><span className="text-xs">{it.planned_date ? new Date(it.planned_date).toLocaleDateString('it-IT') : <span className="text-muted-foreground">—</span>}</span></TableCell>
                      <TableCell><Badge variant={st.variant}>{st.label}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 rounded-full bg-secondary overflow-hidden">
                            <div className="h-full bg-primary transition-all" style={{ width: `${perc}%` }} />
                          </div>
                          <span className="text-xs tabular-nums text-muted-foreground">{perc}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/installer/interventi/${it.id}`} data-testid={`intervento-open-${it.id}`}>
                            <ExternalLink className="size-3.5 mr-1" />Apri
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
